import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot, UrlTree } from '@angular/router';
import { OrderUpsell } from '@models/order-upsell';
import { Recharge } from '@models/recharge';
import { ConsultationRequestService } from '@services/consultation-request.service';
import { ConsultationStorageService } from '@services/consultation-storage.service';
import { ErrorHandlerService } from '@services/error-handler.service';
import { ErrorLoggingService } from '@services/error-logging.service';
import { NavigationService } from '@services/navigation.service';
import { OrderService } from '@services/order.service';
import { OrderPaymentService } from '@services/order-payment.service';
import { StorageService } from '@services/storage.service';
import { TreatmentConsultationQuestionnaireService } from '@services/treatment-consultation-questionnaire.service';
import { TreatmentConsultationSubmissionService } from '@services/treatment-consultation-submission.service';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ConsultationUpsellPaymentGuard implements CanActivate {
  private navigationService = inject(NavigationService);
  private treatmentConsultationSubmissionService = inject(TreatmentConsultationSubmissionService);
  private consultationStorageService = inject(ConsultationStorageService);
  private storage = inject(StorageService);
  private treatmentConsultationQuestionnaireService = inject(TreatmentConsultationQuestionnaireService);
  private errorLoggingService = inject(ErrorLoggingService);
  private errorHandlerService = inject(ErrorHandlerService);
  private orderPaymentService = inject(OrderPaymentService);
  private orderService = inject(OrderService);
  private consultationRequestService = inject(ConsultationRequestService);

  /**
   * Determines whether the current route can be activated.
   *
   * @param {ActivatedRouteSnapshot} _route the route to be activated
   * @param {RouterStateSnapshot} state the current router state
   *
   * @returns {Observable<boolean | UrlTree>} an observable that emits true if the route can be activated, or a UrlTree
   * to redirect otherwise
   */
  public canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    const upsells = this.treatmentConsultationQuestionnaireService.getUpsellsWithPrice();
    const hasBillableUpsells = upsells.some((upsell) => !!upsell.upsell_price);
    const hasOtherCharges = !!this.consultationStorageService.remainingBalance || !!this.storage.inHomeBookingResult;

    if (!hasBillableUpsells && !hasOtherCharges) {
      return this.submitAndNavigate(state.url);
    }

    // TODO: https://github.com/Medology/ng-checkout/issues/4521 - check remaining balance for subscription orders
    if (this.consultationRequestService.isExternalSubscription) {
      return this.submitUpsellsAndNavigateForExternalSubscription(state.url, upsells);
    }

    const recharge = this.consultationStorageService.recharge;

    return recharge ? this.submitAndNavigate(state.url, { recharge }) : of(true);
  }

  /**
   * Submits the treatment consultation and navigates to the next page.
   *
   * @param {string} currentUrl the current URL from which to determine the next page URL
   * @param {object} options optional recharge payment information for upsells, and whether the upsells were already
   * stored by the caller
   */
  private submitAndNavigate(
    currentUrl: string,
    options: { recharge?: Recharge; skipUpsellSubmission?: boolean } = {},
  ): Observable<boolean> {
    return this.getRechargeObservable(options.recharge).pipe(
      switchMap(() =>
        this.treatmentConsultationSubmissionService.submit({ skipUpsellSubmission: options.skipUpsellSubmission }),
      ),
      switchMap((consultationResponse) =>
        this.navigationService.handlePostConsultationUpdateNavigation(
          consultationResponse?.schedule_once_id || this.consultationStorageService.scheduleOnceId,
          currentUrl,
        ),
      ),
      catchError((error) => {
        if (!this.errorHandlerService.isCreditCardDeclineError(error)) {
          this.errorLoggingService.logHttpError(error, 'Failed to submit - Consultation Upsell/Payment', {
            rechargeAttempted: !!options.recharge,
            consultationId: this.consultationStorageService.consultationId,
          });
        }

        return options.recharge ? of(true) : throwError(() => error);
      }),
    );
  }

  /**
   * Stores the upsells of an order that belongs to an external subscription and continues with the submission.
   *
   * No payment is processed by the checkout. If store fails, nothing is stored because, so the route is activated to
   * let the patient provide a new payment method, consistent with how a failed recharge is handled.
   *
   * @param {string}        currentUrl the current URL from which to determine the next page URL
   * @param {OrderUpsell[]} upsells    the upsells selected during the consultation request
   */
  private submitUpsellsAndNavigateForExternalSubscription(
    currentUrl: string,
    upsells: OrderUpsell[],
  ): Observable<boolean> {
    return this.storeUpsellsForExternalSubscription(upsells).pipe(
      switchMap((stored) => (stored ? this.submitAndNavigate(currentUrl, { skipUpsellSubmission: true }) : of(true))),
    );
  }

  /**
   * Stores the upsells of an external subscription order, flagging the order as already upsold on success so the
   * patient cannot re-enter the flow and be charged twice.
   *
   * @param {OrderUpsell[]} upsells the upsells selected during the consultation request
   *
   * @returns {Observable<boolean>} true when the upsells were stored, false when the request failed
   */
  private storeUpsellsForExternalSubscription(upsells: OrderUpsell[]): Observable<boolean> {
    const orderId = this.consultationStorageService.transactionId;

    return this.orderService.submitUpsells(upsells, orderId, this.consultationStorageService.hash).pipe(
      tap(() => (this.consultationStorageService.upsell = [orderId])),
      map(() => true),
      catchError((error) => {
        this.errorLoggingService.logHttpError(
          error,
          'Failed to store subscription upsells - Consultation Upsell/Payment',
          { transactionId: orderId, consultationId: this.consultationStorageService.consultationId },
        );

        return of(false);
      }),
    );
  }

  /**
   * Processes recharge payment if recharge information is provided.
   *
   * @param {Recharge} recharge recharge object containing payment information
   *
   * @returns {Observable<null>} observable that always emits null after processing
   */
  private getRechargeObservable(recharge: Recharge): Observable<null> {
    return recharge
      ? this.orderPaymentService
          .store(
            this.treatmentConsultationSubmissionService.createPaymentRequestFromRecharge(recharge),
            this.consultationStorageService.hash,
          )
          .pipe(
            tap((response) => (this.consultationStorageService.upsell = [response.transaction_id])),
            map(() => null),
          )
      : of(null);
  }
}
